import type { AppBundle } from '../../utils/app-bundles';
import type { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import type { AccountPermission } from '@customerio/hydra-permissions';
import type { UserRole } from '@/features/roles';
import type {
  Account as ApiAccount,
  User as ApiUser,
  PromoCode,
  UserGoogleDriveCredential,
  UsersSideNavPreferences,
} from '@customerio/hydra-ui-api';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';

export const VerificationState = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  Unsubmitted: 'unsubmitted',
} as const;
export type VerificationState = (typeof VerificationState)[keyof typeof VerificationState];

export const VerificationReviewState = {
  Pending: 'pending',
  Enriching: 'enriching',
  Deferred: 'deferred',
  Review: 'review',
  Approved: 'approved',
  Rejected: 'rejected',
  NeedsMoreInfo: 'needs_more_info',
} as const;
export type VerificationReviewState = (typeof VerificationReviewState)[keyof typeof VerificationReviewState];

export interface Account {
  id: number;
  created_at: number;
  name: string;
  /** The account owner's email — the default test-delivery address. */
  email: string;
  invoice_email: string;
  technical_email: string;
  legal_email: string;
  is_sample_account_access: boolean;
  is_masq: boolean;
  /**
   * Whether the account mandates 2FA for every member. On the session account
   * so the personal security page can read it without a second fetch.
   */
  two_factor_authentication_required: boolean;
  /** Whether the account's subscription is paused. */
  is_paused: boolean;
  write_access: boolean;
  is_own_account: boolean;
  feature_flags: Record<string, boolean>;
  logo_url: string;
  plan_slug: string;
  premium: boolean;
  premium_trial: boolean;
  /** Account-level partner flag (set in Flightdeck). Gates the Partner section. */
  partner_account: boolean;
  plan_features: PlanFeature[];
  gated_features: GatedFeatures;
  admin: boolean;
  product_activations: AppBundle[];
  region: Region;
  support_tier: SupportTier;
  suspend_trial_at?: number;
  /** Unix timestamp (seconds) when a canceled account is scheduled for deletion. */
  delete_at?: number;
  /**
   * If policy permits a self-serve reactivation. The backend decides this from the breach reason and the
   * reactivation limit. The account condition is a separate answer, which the billing service gives, and
   * the caller must have both.
   */
  can_reactivate?: boolean;
  account_state: string;
  /** SSO enforcement level ('required' when SSO is mandatory). Drives in-place re-auth variant selection. */
  sso_setting: string;
  /** Current billing period start/end (unix seconds). Used by the billing service's
   * billingPeriodStart/billingPeriodEnd for non-Ordway accounts. */
  plan_period_start: number;
  plan_period_end: number;
  /** Whether churned profiles are billable for the account's plan. Used by the billing usage displays. */
  plan_include_churn?: boolean;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  access_granted_until?: number;
  access_granted_by?: string;
  access_granted_on?: number;
  data_center: 'us' | 'eu';
  enable_troubleshooting_cookies: boolean;
  has_csm: boolean;
  cs_access_granted_until?: number;
  cs_access_granted_by?: string;
  cs_access_granted_on?: number;
  ordway_id?: string;
  billing_vendor?: BillingVendor;
  /**
   * Promo codes redeemed by the account (e.g. Lenny's Product Pass). Drives the promo-checkout gate
   * (`requiresCardCheckout`): an Ordway account that redeemed a promo but hasn't added a card yet.
   */
  promo_codes?: PromoCode[];
  read_only: boolean;
  /**
   * Whether the account may only send email through a custom SMTP server. Such
   * an account must keep at least one, so its last server can't be deleted.
   */
  custom_smtp_locked: boolean;
  /** Whether the account owner has confirmed their email address. */
  confirmed?: boolean;
  verification_state: VerificationState;
  verification_review_state?: VerificationReviewState;
  sandbox: boolean;
  /**
   * Account-level customer opt-in settings. `ai_enabled` gates Customer.io AI
   * (formerly the `ai_enabled` feature flag). Omitted keys are treated as false.
   */
  public_settings?: AccountPublicSettings;
  /**
   * What the user told us at signup (collected since Feb 2026). Absent for
   * older accounts and for backends that predate the
   * field — treat missing as "didn't answer". The backend normalizes the
   * legacy literal `"None"` role to empty.
   */
  signup_profile?: SignupProfile | null;
}

/** Mirrors `services_domains_accounts_SignupProfile` on the account bootstrap. */
export interface SignupProfile {
  role: string;
  messaging_use_cases: string[];
  channels: string[];
}

/** Account-level customer opt-in settings, stored in `accounts.public_settings`. */
export interface AccountPublicSettings {
  ai_enabled?: boolean;
  use_global_ai_endpoint?: boolean;
}

/**
 * The account payload exactly as `/v1/accounts/:id` returns it. Aliased to the
 * generated model so the wire's own nullability (several fields are `T | null`)
 * is what the auth mappers see, instead of a hand-maintained copy that has to
 * be asserted onto the response.
 */
export type RawAccount = ApiAccount;

interface PlanFeatureInfo {
  expired: boolean;
  enabled: boolean;
  showPremiumLabel: boolean;
  limit: number | false;
  expiresAt: number | null;
}

export interface AugmentedAccount extends Account {
  // active products as flags for easy access
  activeProducts: Record<AppBundle, boolean>;
  // plan features computed property
  planFeatures: Record<PlanFeature, PlanFeatureInfo>;
  // support team access enabled (computed from access_granted_until)
  support_team_access_enabled: boolean;
  // success team access enabled (computed from cs_access_granted_until)
  success_team_access_enabled: boolean;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  account_id: number;
  last_cio_workspace_id: string;
  account_permissions: AccountPermission[];
  /** Workspace ids this user can access. Empty means the user has no workspace access yet. */
  accessible_environment_ids: string[];
  roles: UserRole[];
  masq_permissions: UserMasqPermission;
  can_masquerade: boolean;
  can_masquerade_write: boolean;
  user_flags: { [key: string]: boolean };
  settings?: UserSettings;
  google_drive_credential?: UserGoogleDriveCredential | null;
  /** SSO/one-click identity provider from the user's last login, '' if none. Drives in-place re-auth variant selection. */
  external_identity_provider: string;
  external_identity_email: string;
  /** Whether the signed-in user has set up authenticator-app 2FA on their personal account. */
  two_factor_authentication_enabled: boolean;
  /** URL of the user's uploaded profile image; `null` when none is set. */
  profile_image_url: string | null;
  /** Whether required email-link 2FA applies to the user. */
  two_factor_authentication_email_enabled: boolean;
  /**
   * Largest file this user may upload to an import, in bytes. The bootstrap has always carried it
   * and the mapping dropped it, so the upload screen had no limit to show or check against — the
   * sign endpoint enforces the real one either way.
   */
  import_max_size_bytes: number;
}

/**
 * The user payload exactly as `/v1/accounts/:id` returns it. Aliased to the
 * generated model for the same reason as {@link RawAccount}: the wire's
 * nullability and its loose `account_permissions`/`masq_permissions` strings are
 * what `getUserFromRaw` has to narrow, so they belong in the type.
 */
export type RawUser = ApiUser;

// Mirrors services_domains_users_ThemePreferences; can be swapped for the generated
// `UsersThemePreferences` type once the hydra-ui-api client is regenerated post-merge.
export interface UsersThemePreferences {
  color_scheme: ColorSchemePreference;
}

export interface UserSettings {
  side_nav_preferences?: UsersSideNavPreferences;
  theme_preferences?: UsersThemePreferences;
}

export interface AugmentedUser extends User {
  name: string; // computed property for full name
  // masq permissions computed properties
  isSuperUser: boolean;
  canReadOnlyMasq: boolean;
  canReadWriteMasq: boolean;
  noMasqAccess: boolean;
  masqLevelNotSet: boolean;
  hasReadMasqAccess: boolean;
}

export type SupportTier = 'community' | 'email' | 'chat' | '';

/**
 * Billing system backing the account.
 *
 * Derived from the generated `hydra-ui-api` account model rather than hand-written, so the set cannot
 * drift from the wire contract when the backend adds a vendor. This is the single definition — the
 * `BillingSystem` discriminant in `services/billing/types.ts` and the `window.__hydra__.account`
 * declaration both check themselves against it.
 *
 * The generated field is `billing_vendor?: ... | null`, but the `| null` is a codegen artifact:
 * `services/ui_api/model/account.go:51` declares a plain `string`, not a pointer, so the JSON never
 * contains `null`. `NonNullable` strips it here, and the account fields below are optional only.
 *
 * They are genuinely **optional**, though — the API omits the field for accounts whose column was
 * never set. Anything asking "which vendor is this" must go through
 * `services/billing/resolve-vendor.ts`, which mirrors the backend's own fallback; a bare
 * `=== 'legacy_stripe'` silently misreads those accounts.
 */
export type BillingVendor = NonNullable<ApiAccount['billing_vendor']>;

export const AccountState = {
  Free: 'free',
  Current: 'current',
  Canceled: 'canceled',
  Suspended: 'suspended',
  Overdue: 'overdue',
  PaymentPending: 'payment_pending',
  Deleted: 'deleted',
  PendingCancellation: 'pending_cancellation',
} as const;

type AccountState = (typeof AccountState)[keyof typeof AccountState];

/**
 * A `gated_features` entry. The first four members come straight off the wire
 * (which sends `null` for an unset number and an RFC3339 string for `start_at`;
 * `Date`/epoch values come from fixtures and the Ember-era callers); `expired`
 * and `expiresAt` are computed by `augmentAccount`.
 */
export interface PlanFeatureLimit {
  limit?: number | null;
  days?: number | null;
  start_at?: Date | number | string;
  /** Set when the feature is being trialled rather than granted outright. */
  is_trial?: boolean | null;
  is_cancelled?: boolean | null;
  is_suspended?: boolean | null;
  expired?: boolean;
  expiresAt?: number;
}

export interface GatedFeatures {
  [key: string]: PlanFeatureLimit | Record<string, never> | undefined;
}

export const Region = {
  US: 0,
  EU: 1,
} as const;

export type Region = (typeof Region)[keyof typeof Region];

export const PlanFeature = {
  Workspaces: 'workspaces',
  WarehouseSync: 'warehouse_sync',
  Collections: 'collections',
  ObjectTypes: 'object_types',
  Messaging: 'messaging',
  MaxObjectJourneys: 'max_object_journeys',
  SupportTierChat: 'support_tier_chat',
  SupportTierCommunity: 'support_tier_community',
  SupportTierEmail: 'support_tier_email',
  PremiumTrial: 'premium_trial',
  CDPPremiumIntegration: 'cdp_premium_integrations',
  AnonymousInApp: 'anonymous_in_app',
  RoutinesActive: 'routines_active',
  CustomSkills: 'custom_skills',
  AuditLogs: 'audit_logs',
  DeliverabilityAnalytics: 'deliverability_analytics',
  CustomRoles: 'custom_roles',
  Geofences: 'geofences',
  LiveNotifications: 'live_notifications',
  // Gate the workspace role redaction options (mirrors the Ember account model's
  // `sensitive_attributes` / `hide_all_attributes` plan features).
  SensitiveAttributes: 'sensitive_attributes',
  HideAllAttributes: 'hide_all_attributes',
  // Compliance control (SPE-3140): caps or blocks sending-domain creation.
  // Set per account from Flightdeck; not a plan feature, so no premium bypass.
  SendingDomains: 'sending_domains',
} as const;

export type PlanFeature = (typeof PlanFeature)[keyof typeof PlanFeature];

interface IdentifiersConfig {
  cio_id: number;
  id: number;
  email: number;
}

export interface Workspace {
  id: string;
  account_id: string;
  workspace_id: string;
  name: string;
  delivery_settings: string;
  delivery_email: string;
  user_ids: string[];
  hex_code: string;
  created_at: number;
  identifier_type: string;
  identifiers?: IdentifiersConfig;
  type: string;
  stop_processing: boolean;
  email_open_tracking: boolean;
  subscription_center_enabled: boolean;
  email_delivery_disabled: boolean;
}

interface BaseSearchResult {
  doc_id: string;
  kind: string;
  title: string;
  url: string;
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  other?: Record<string, any>;
  /** Vertex AI relevance score (0–1, bucketed). Present when the backend sets returnRelevanceScore=true; omitted when Vertex does not return it for a given result. */
  relevance_score?: number;
  /**
   * Matched-text excerpts (with <b> highlight tags) returned by Discovery Engine for
   * content-bearing datastores — templates, snippets, and anonymous in-app. Absent for
   * structured entities and when the match was on a field rather than the body. Mirrors the
   * backend's base SearchResult.Snippets (services/universal_search, json: "snippets,omitempty").
   */
  snippets?: string[];
}

export type CampaignType =
  | 'seg_attr'
  | 'transactional'
  | 'behavioral'
  | 'triggered_broadcast'
  | 'date'
  | 'form'
  | 'webhook'
  | 'object'
  | 'relationship'
  | 'none';

interface CampaignResult extends BaseSearchResult {
  kind: 'campaign';
  other: {
    campaign_type: CampaignType;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    archived: string;
  };
}

export type ActionType =
  | 'email_action'
  | 'delay_seconds_action'
  | 'delay_time_window_action'
  | 'split_randomized_action'
  | 'webhook_action'
  | 'twilio_action'
  | 'urban_airship_action'
  | 'slack_action'
  | 'attribute_update_action'
  | 'filter_match_delay_action'
  | 'grace_period_action'
  | 'push_action'
  | 'conditional_wait_action'
  | 'randomized_delay_action'
  | 'conditional_branch_action'
  | 'multi_split_branch_action'
  | 'random_cohort_branch_action'
  | 'exit_action'
  | 'static_seg_update_action'
  | 'collection_query_action'
  | 'create_event_action'
  | 'multi_lang_branch_action'
  | 'batch_update_action'
  | 'in_app_action'
  | 'trigger'
  | 'llm_action'
  | 'line_action'
  | 'inbox_action'
  | 'whatsapp_action';

interface ActionResult extends BaseSearchResult {
  kind: 'action';
  other: {
    action_type: ActionType;
    campaign_id: string;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    campaign_name?: string;
    campaign_url?: string;
  };
}

type SegmentType = 'static' | 'dynamic' | 'temporary' | 'hidden';

interface SegmentResult extends BaseSearchResult {
  kind: 'segment';
  other: {
    segment_type: SegmentType;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    archived: string;
  };
}

export type TemplateType =
  | 'email'
  | 'webhook'
  | 'twilio'
  | 'urban_airship'
  | 'slack'
  | 'push'
  | 'create_event'
  | 'attribute_update'
  | 'batch_update_not_configured'
  | 'batch_create_event'
  | 'batch_attribute_update'
  | 'in_app'
  | 'in_app_broadcast'
  | 'llm_action_prompt'
  | 'llm_action_result_schema'
  | 'line'
  | 'inbox'
  | 'whatsapp'
  // Newsletters are created with channel "unset" while the user picks a channel in a later setup
  // step. The backend indexes the underlying template with template_type "unset" before that happens.
  // https://github.com/customerio/ui/blob/main/app/pods/broadcasts/new/controller.ts#L113
  // https://github.com/customerio/services/blob/main/ui_api/newsletters/create.go#L42
  | 'unset';

interface TemplateResult extends BaseSearchResult {
  kind: 'template';
  other: {
    template_type: TemplateType;
    subject?: string;
    language?: string;
    action_id?: string;
    newsletter_id?: string;
    transactional_message_id?: string;
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
  };
}

interface NavigationResult extends BaseSearchResult {
  kind: 'navigation';
  title: string;
  url: string;
  description?: never; // Navigation items don't have descriptions
  other?: never; // Navigation items don't have other
  snippets?: never; // Navigation items don't have snippets
}

interface NewsletterResult extends BaseSearchResult {
  kind: 'newsletter';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    sent: string;
  };
}

interface GoalResult extends BaseSearchResult {
  kind: 'goal';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    goal_event?: string;
  };
}

interface TransactionalMessageResult extends BaseSearchResult {
  kind: 'transactional_message';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    // Mirrors services/domains/transactional/message.go TransactionalMessage.TypeToString.
    // `twilio` is the legacy on-the-wire value for SMS.
    message_type?: 'email' | 'push' | 'in_app' | 'twilio' | 'inbox' | 'unknown';
    state?: 'draft' | 'active';
  };
}

// Mirrors services/domains/in_app_broadcasts/in_app_broadcasts.go State.String(). `deleting`
// is omitted because that state is tombstoned during indexing and never reaches search.
export type AnonymousInAppState = 'drafted' | 'scheduled' | 'starting' | 'running' | 'stopping' | 'expired' | 'stopped';

// Anonymous In-App Messages are backed by the in_app_broadcasts table. The search document
// shape comes from services/universal_search/anonymous_in_app.go (anonymousInAppRecord.Fields).
interface AnonymousInAppResult extends BaseSearchResult {
  kind: 'anonymous_in_app';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    state?: AnonymousInAppState;
  };
}

// Snippets are reusable Liquid fragments backed by the global_variables table. The search
// document shape comes from services/universal_search/snippets.go: title, url, and updated_at
// are indexed as fields, and the Liquid value is indexed as searchable content (surfaced as
// matched-text excerpts in the base `snippets` field).
interface SnippetResult extends BaseSearchResult {
  kind: 'snippet';
  other: {
    updated_at: string;
    env_id: string;
    id: string;
    // Explicit undefined so `item.other?.created_at != null` narrows the SearchResult union.
    // Snippets only index title, url, and updated_at — there is no created_at.
    created_at?: undefined;
  };
}

// Email Layouts are the reusable HTML wrappers (header/footer around a {{content}} placeholder)
// backed by the email_layouts table. The search document shape comes from
// services/universal_search/email_layouts.go (emailLayoutRecord.Fields) — services serialises the
// layout's archived flag as the string "true"/"false".
interface EmailLayoutResult extends BaseSearchResult {
  kind: 'email_layout';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    archived?: 'true' | 'false';
  };
  // Email layouts are content-bearing — the backend indexes the HTML body, so search
  // returns highlighted body snippets via the inherited BaseSearchResult.snippets.
}

// Collections are reusable datasets (the collections table) imported from CSV / Google Sheets /
// URL / Amazon S3 and referenced by Liquid in messages. The search document shape comes from
// services/universal_search/collections.go (collectionRecord.Fields). The url is the Fly app link
// to the collection itself (not the import source url), so a result navigates into the workspace.
interface CollectionResult extends BaseSearchResult {
  kind: 'collection';
  other: {
    created_at: string;
    updated_at: string;
    env_id: string;
    id: string;
    description?: string;
    // Import source kind: 's3' | 'sheet' | 'url'; empty for a manually-built collection.
    import_type?: string;
    // services serialises the bool via convertToSearchResult, so it arrives as "true"/"false".
    importing?: 'true' | 'false';
    // Row count as a numeric string (e.g. "1234"): convertToSearchResult only surfaces string/bool
    // struct fields, so services indexes it as a string to keep it retrievable in search results.
    rows_number?: string;
  };
  // Collections index only their metadata (name + description), not the row data, so search never
  // returns body snippets via the inherited BaseSearchResult.snippets.
}

export interface CustomerResult extends BaseSearchResult {
  kind: 'customer';
  other: {
    id?: string;
    email?: string;
    unsubscribed?: boolean;
    created_at?: string;
  };
}

export interface DocsResult extends BaseSearchResult {
  kind: 'docs';
  other: {
    description: string;
    // Explicit undefined so `item.other?.created_at != null` narrows the SearchResult union
    created_at?: undefined;
  };
}

type IntegrationResultOther = {
  type: string;
  enabled: boolean;
  status: string;
  created_at?: number;
  last_activity?: number;
};

export interface SourceResult extends BaseSearchResult {
  kind: 'source';
  other: IntegrationResultOther;
}

export interface DestinationResult extends BaseSearchResult {
  kind: 'destination';
  other: IntegrationResultOther;
}

export interface CustomFormResult extends BaseSearchResult {
  kind: 'custom_form';
  other: {
    env_id: number;
    form_type: string;
    form_id: string;
    status: string;
    created_at?: number;
    last_activity_at?: number;
    html_form_url?: string;
  };
}

export interface ReportingWebhookResult extends BaseSearchResult {
  kind: 'reporting_webhook';
  other: {
    enabled: boolean;
    created_at?: number;
  };
}

export interface CdpOutResult extends BaseSearchResult {
  kind: 'cdp_out';
  other: {
    enabled: boolean;
    created_at?: number;
  };
}

export type DataOutExporterType = 'mixpanel' | 'amplitude' | 'segment_out' | 'mparticle';

export interface DataOutResult extends BaseSearchResult {
  kind: 'data_out';
  other: {
    exporter_type: DataOutExporterType;
    enabled: boolean;
    created_at?: number;
  };
}

export interface ObjectTypeResult extends BaseSearchResult {
  kind: 'object_type';
  other: {
    id: number;
    env_id: number;
    slug: string;
    singular_name: string;
    object_count: number;
    icon?: string;
    // Explicit undefined so `item.other?.created_at != null` narrows the SearchResult union
    created_at?: undefined;
  };
}

export type SearchResult =
  | CampaignResult
  | ActionResult
  | SegmentResult
  | TemplateResult
  | NavigationResult
  | NewsletterResult
  | GoalResult
  | TransactionalMessageResult
  | AnonymousInAppResult
  | SnippetResult
  | EmailLayoutResult
  | CollectionResult
  | CustomerResult
  | DocsResult
  | SourceResult
  | DestinationResult
  | AdAudienceResult
  | CustomFormResult
  | ReportingWebhookResult
  | CdpOutResult
  | DataOutResult
  | ObjectTypeResult;

export type AdAudienceCredentialType = 'facebook' | 'adwords';

export interface AdAudienceResult extends BaseSearchResult {
  kind: 'ad_audience';
  other: {
    credential_type: AdAudienceCredentialType;
    expired: boolean;
    created_at?: number;
  };
}
